import { useEffect, useState } from 'react';
import type { AgentState } from '../types';
import { isStale, relativeTime, classNames } from '../util';

interface Props {
  agents: AgentState[];
}

export function AgentsTab({ agents }: Props) {
  const [, setTick] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Re-render every 5s for relative-time + staleness updates
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, []);

  const sorted = [...agents].sort((a, b) => a.agent_id.localeCompare(b.agent_id));

  return (
    <div className="tab agents">
      <h1 className="tab-title">Agents</h1>
      {sorted.length === 0 && (
        <div className="muted">
          No agents heartbeating yet. Start an MCP bridge and they'll appear here.
        </div>
      )}
      <ul className="agent-list">
        {sorted.map((a, idx) => {
          const stale = isStale(a.last_heartbeat, 30000);
          const open = expanded === a.agent_id;
          const showHint = idx === 0 && expanded === null;
          return (
            <li
              key={a.agent_id}
              className={classNames('agent-card', stale && 'is-stale', open && 'is-open')}
            >
              <button
                className="agent-head"
                onClick={() => setExpanded(open ? null : a.agent_id)}
                aria-expanded={open}
                aria-label={`${a.agent_id} ${open ? 'collapse' : 'expand'}`}
              >
                <div className="agent-head-main">
                  <div className="agent-id">
                    <span className="agent-dot" aria-hidden />
                    {a.agent_id}
                    {stale && <span className="agent-stale-badge">stale</span>}
                    {showHint && <span className="agent-tap-hint">tap to expand</span>}
                  </div>
                  <div className="agent-status">{a.status_text || 'idle'}</div>
                  <div className="agent-meta">
                    {a.current_file && <span className="agent-file">{a.current_file}</span>}
                    <span className="agent-time">{relativeTime(a.last_heartbeat)}</span>
                    {a.pending_questions > 0 && (
                      <span className="agent-q-badge">{a.pending_questions} Q</span>
                    )}
                  </div>
                </div>
                <span className={classNames('agent-chevron', open && 'is-open')} aria-hidden>
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </span>
              </button>
              {open && (
                <div className="agent-detail">
                  <div className="kv">
                    <span>session</span>
                    <code>{a.agent_session}</code>
                  </div>
                  <div className="kv">
                    <span>worktree</span>
                    <code>{a.worktree_path}</code>
                  </div>
                  <div className="kv">
                    <span>branch</span>
                    <code>{a.current_branch}</code>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
